import { useMemo, useEffect } from "react";

import { useDispatch } from "react-redux";

import useDecodedParams from "Hooks/useDecodedParams";
import { getQueryInterval } from "src/organization/pages/project/environment/metrics/components/Chart/settings";

import { DG3_QUERY, timeframes } from "../resources/settings";
import { clearCache } from "../utils/parseDimensions";
import sortAlphabetically from "../utils/sortAlphabetically";
import {
  explodeMetrics,
  groupByTimestamp,
  mergeSameTimestampData
} from "../utils/transformMetrics";

import useMetrics from "./useMetrics";
import useRange from "./useRange";
import useServices from "./useServices";

// user zoom must span at least this range (in seconds) to ensure at least 3 data points display
const minimumRange = 180;

//TODO: remove mergeHostTimestamp when useHost is refactored to return full host name instead of host id number
const mergeHostTimestamp = data => mergeSameTimestampData(data);

const getServiceMetrics = (serviceId, metrics) => {
  if (!metrics?.length || !serviceId) {
    return undefined;
  }
  const serviceMetrics = metrics.filter(point => point.service === serviceId);
  const mountpoints = sortAlphabetically(
    Array.from(
      new Set(
        metrics
          .map(({ mountpoint }) => mountpoint)
          .filter(mountpoint => mountpoint != null)
      )
    )
  ).reduce((mountpoints, mountpoint) => {
    const mountpointMetrics = groupByTimestamp(
      serviceMetrics.filter(point => point.mountpoint === mountpoint)
    ).map(mergeHostTimestamp);

    if (mountpointMetrics.length) {
      return {
        ...mountpoints,
        [mountpoint]: mountpointMetrics
      };
    }

    return mountpoints;
  }, {});
  const cpu = groupByTimestamp(
    serviceMetrics.filter(point => point.type === "cpu")
  ).map(mergeHostTimestamp);

  const memory = groupByTimestamp(
    serviceMetrics.filter(point => point.type === "memory")
  ).map(mergeHostTimestamp);
  return {
    ...mountpoints,
    cpu,
    memory
  };
};

const getServiceData = (services, metrics) => {
  if (!services || !metrics) {
    return undefined;
  }

  return services.map(service => ({
    ...service,
    metrics: getServiceMetrics(service.name, metrics)
  }));
};

const getMetricsRange = metrics => {
  if (!metrics?.metrics?.length) {
    return [undefined, undefined];
  }
  const from = metrics.metrics[0].point.timestamp;
  const to = metrics.metrics[metrics.metrics.length - 1].point.timestamp;
  return [from, to];
};

const useDedication = (chorusURL, collection) => {
  const { organizationId, projectId, environmentId } = useDecodedParams();
  const dispatch = useDispatch();
  const { selectedTimeframe, setSelectedTimeframe } = useRange(
    timeframes,
    minimumRange
  );

  const range = useMemo(() => {
    if (selectedTimeframe.range) {
      return selectedTimeframe.range;
    }

    return { from: selectedTimeframe.from, to: selectedTimeframe.to };
  }, [selectedTimeframe]);
  const query = useMemo(
    () => ({
      interval: getQueryInterval(selectedTimeframe),
      fields: DG3_QUERY,
      stream: {
        collection: collection,
        stream: "metrics"
      }
    }),
    [selectedTimeframe]
  );
  const { data, isLoading, error } = useMetrics(chorusURL, query, range);
  const [services, hosts, isLoadingSerivces] = useServices(
    organizationId,
    projectId,
    environmentId,
    collection,
    chorusURL,
    60 * 15,
    true
  );

  const servicesWithData = useMemo(
    () => getServiceData(services, explodeMetrics(data)),
    [services, data]
  );

  useEffect(() => () => clearCache(), []);

  const metricsRange = useMemo(() => getMetricsRange(data), [data]);

  return [
    servicesWithData,
    hosts,
    metricsRange,
    selectedTimeframe,
    setSelectedTimeframe,
    isLoading || isLoadingSerivces,
    error
  ];
};

export default useDedication;
